/**
 * ER (Email Remediator) module API wrapper.
 *
 * All ER backend controllers live under `/v1/er/*`. `erFetch` prefixes `/er/`
 * and routes through the universal `fetchApi` client so the admin bearer token +
 * `x-workspace-id` header are attached automatically (tenant isolation rides in
 * that header — ER query keys carry NO workspaceId and NO mode; see
 * lib/er/query-keys.ts). Mirrors the ESA wrapper (lib/esa/api.ts).
 *
 * CSV export endpoints stream a `StreamableFile`, so they cannot go through
 * `fetchApi` (which JSON-parses the body) — `erDownload` does a raw authed fetch
 * and returns a Blob for those.
 *
 * The thin per-endpoint wrappers below cover every ER route grouped by
 * controller. Every ER data call MUST go through `erFetch` / `erApi.*` — never a
 * bare `fetch('/api/...')`.
 */
import { fetchApi, ApiClientError, getAccessToken, getWorkspaceId } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/api/config'
import { filenameFromContentDisposition } from '@/lib/http/content-disposition'
import { readSelectedDomainId } from '@/lib/tenant/domain-selection'
import { withStaleDomainRecovery } from '@/lib/tenant/stale-domain-recovery'
import type {
  ReportedMail,
  ReportedMailDetail,
  ReportedMailTimelineEntry,
  ErRemediationEnqueueResult,
  ErNotifyResult,
  Disposition,
  EventAction,
  TrustedDomain,
  ErIntegration,
  ErTemplate,
  ErEffectiveTemplate,
  ErDefaultTemplateSettings,
  ReporterMessage,
  AddinAction,
  ReporterPoint,
  SnrSearch,
  SnrProgress,
  SnrMessage,
  SnrRemediationJob,
  DashboardStat,
  ErStatusBucketCount,
  ErRemediationTrendPoint,
  ErBreakdownRow,
  ErAuthFailureChart,
  ErCategorySeries,
  ErBreakdownSlice,
  ErAntiphishOverrideRow,
  ErUserGroupOverview,
  ErDomainStat,
  ErThreatLogDashboard,
  ErReportRow,
  ErThreatLog,
  LeaderboardResponse,
  LeaderboardStats,
  EmailLog,
  NotificationLog,
  BannerLog,
  BannerLogUser,
  AiActionLog,
  ErPolicy,
  ErListEnvelope,
  ErUrlRescanResult,
  ErAttachmentRescanResult,
  ErReinstateResult,
  ErForwardResult,
  ErNotifySocResult,
  ErReportMsResult,
  ErOrgRemediationResult,
  ErMicrosoftMailType,
  ErSnrAction,
  ErStatusBucketCount as ErReportedMailStatusBucket,
  ErAddinBrandingEditorResponse,
  ErAddinBrandingUpdateResponse,
  ErAddinBrandingForEditor,
  ErAddinBrandingResolved,
  ErAddinContrastViolation,
} from './types'
import type {
  CreateErDispositionForm,
  UpdateErDispositionForm,
  CreateErEventActionForm,
  UpdateErEventActionForm,
  ReorderErEventActionsForm,
  CreateErTrustedDomainForm,
  ReportToMicrosoftConfigForm,
  SocRecipientsConfigForm,
  UpdateErIntegrationConfigForm,
  CreateErTemplateForm,
  UpdateErTemplateForm,
  UpdateErDefaultTemplateSettingsForm,
  UpdateReporterMessagesForm,
  UpdateAddinActionsForm,
  UpdateReporterPointsForm,
  UpdateErAddinBrandingForm,
  UpdateErSettingsForm,
  ErUpdateStatusForm,
  ErBulkUpdateStatusForm,
  ErNotifyReporterForm,
  StartSnrSearchForm,
  SnrRemediateForm,
  SnrRemediateMessageForm,
} from './schemas'

// ═══════════════════════════════════════════════════════════════════════════════
// Envelope + select helpers (mirror lib/esa/api.ts)
// ═══════════════════════════════════════════════════════════════════════════════

/** Convenience `select` → the row array. Tolerates `{data}` OR `{items}`. */
export const selectEnvelopeData = <T,>(env: ErListEnvelope<T>): T[] =>
  env?.data ?? env?.items ?? []

/** Pagination metadata folded off an ER list envelope. */
export interface ErListMeta {
  total: number
  page: number
  limit: number
  totalPages: number
}

/** Convenience `select` → the real pagination metadata (for "Page 1 of N"). */
export const selectEnvelopeMeta = <T,>(env: ErListEnvelope<T>): ErListMeta => {
  const rows = env?.data ?? env?.items ?? []
  const limit = env?.limit ?? rows.length ?? 0
  const total = env?.total ?? rows.length ?? 0
  return {
    total,
    page: env?.page ?? 1,
    limit,
    totalPages: env?.totalPages ?? Math.max(1, Math.ceil(total / Math.max(1, limit))),
  }
}

/** Typed ER API error (maps `ApiClientError` for call sites that try/catch). */
export class ErApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message)
    this.name = 'ErApiError'
  }
}

/**
 * The ER module's selected domain, resolved by the ER domain context provider
 * (`components/er/shared/er-domain-context.tsx`). Every ER request carries it as
 * an `x-domain-id` header: without it, `TenantContextGuard` resolves the tenant
 * domain to `''` and every domain-scoped ER write (triage actions, settings
 * mutations, banner-remove, reporter-points) 403s/400s.
 *
 * "All domains" sends NO header, so the BE list endpoints see the whole
 * workspace. Note the context persists a SENTINEL for that choice rather than
 * `null` — shipping the sentinel raw is what 500'd every ER route
 * (HFV3-BACKEND-P). The key, the sentinel and the strip now live in one place,
 * shared with ESA, which had already fixed this independently.
 */
function erSelectedDomainId(): string | null {
  return readSelectedDomainId('er')
}

/**
 * Attach `x-domain-id` when a concrete ER domain is selected.
 *
 * Exported for the regression suite: the contract is that the key is ABSENT
 * (not `undefined`, not empty-string) whenever there is no concrete domain.
 */
export function withErDomainHeader(
  headers: Record<string, string>,
): Record<string, string> {
  const domainId = erSelectedDomainId()
  if (domainId) headers['x-domain-id'] = domainId
  return headers
}

/**
 * ER JSON fetch. Author passes the path AFTER `/er/` — the wrapper enforces the
 * prefix so callers cannot accidentally encode `/v1/`, `/api/`, or omit the
 * module prefix. `idempotencyKey` sets the `Idempotency-Key` header.
 */
export async function erFetch<T>(
  path: string,
  options?: RequestInit & { idempotencyKey?: string },
): Promise<T> {
  const cleanPath = path.replace(/^\/+/, '')
  const endpoint = `/er/${cleanPath}`

  const { idempotencyKey, headers: optHeaders, ...rest } = options ?? {}
  const buildHeaders = (): Record<string, string> => {
    const mergedHeaders: Record<string, string> = withErDomainHeader({
      ...(optHeaders as Record<string, string> | undefined),
    })
    if (idempotencyKey) mergedHeaders['Idempotency-Key'] = idempotencyKey
    return mergedHeaders
  }

  try {
    return await withStaleDomainRecovery('er', rest.method, buildHeaders, (headers) =>
      fetchApi<T>(endpoint, { ...rest, headers }),
    )
  } catch (err) {
    if (err instanceof ApiClientError) {
      throw new ErApiError(err.statusCode, err.message, err.body)
    }
    throw err
  }
}

/** Build a stable Idempotency-Key for bulk mutations / imports. */
export function withIdempotencyKey(prefix = 'er'): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `${prefix}-${rand}`
}

/** Download a server-streamed CSV (`StreamableFile`) from an ER export endpoint. */
export async function erDownload(
  path: string,
  options?: RequestInit,
): Promise<Blob> {
  const cleanPath = path.replace(/^\/+/, '')
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/er/${cleanPath}`

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string> | undefined),
  }
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId
  withErDomainHeader(headers)

  const res = await fetch(url, { ...options, headers })
  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = await res.text()
    }
    throw new ErApiError(res.status, res.statusText, body)
  }
  return res.blob()
}

/**
 * Download a password-protected AES-256 zip (attachment quarantine). The BE returns
 * the one-time password in the `X-ZIP-PASSWORD` header (never inside the zip) + the
 * filename — both surfaced here so the caller can show the password to the analyst.
 * Same auth/domain headers as erDownload.
 *
 * The name comes from Content-Disposition's `filename*` first: `X-FILENAME` can
 * only carry Latin-1, so for "فاتورة.pdf" it holds the ASCII fallback and the
 * download was saved as "_.pdf.zip".
 */
export async function erDownloadZip(
  path: string,
): Promise<{ blob: Blob; password: string; fileName: string }> {
  const cleanPath = path.replace(/^\/+/, '')
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/er/${cleanPath}`

  const headers: Record<string, string> = {}
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId
  withErDomainHeader(headers)

  const res = await fetch(url, { headers })
  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = await res.text()
    }
    throw new ErApiError(res.status, res.statusText, body)
  }
  return {
    blob: await res.blob(),
    password: res.headers.get('X-ZIP-PASSWORD') ?? '',
    fileName:
      filenameFromContentDisposition(res.headers.get('Content-Disposition')) ||
      res.headers.get('X-FILENAME') ||
      'attachment.zip',
  }
}

/**
 * POST a multipart `FormData` body to an ER endpoint (e.g. the branding logo
 * upload). `erFetch`/`fetchApi` force `Content-Type: application/json`, which would
 * corrupt a multipart body, so this does a raw authed fetch and — critically — sets
 * NO `Content-Type` header, letting the browser emit `multipart/form-data` + the
 * boundary. Bearer + `x-workspace-id` are attached the same way `erDownload` does.
 */
export async function erUpload<T>(path: string, form: FormData): Promise<T> {
  const cleanPath = path.replace(/^\/+/, '')
  const url = `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/er/${cleanPath}`

  const headers: Record<string, string> = {}
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId
  withErDomainHeader(headers)

  const res = await fetch(url, { method: 'POST', body: form, headers })
  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = await res.text()
    }
    const msg =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : res.statusText
    throw new ErApiError(res.status, msg, body)
  }
  return res.json() as Promise<T>
}

/** Trigger a browser download of a Blob under `fileName`. */
export function saveBlob(blob: Blob, fileName: string): void {
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Serialize a filter object to a query string (skips null/undefined/empty). */
export function erQueryString(
  filters?: Record<string, string | number | boolean | undefined | null>,
): string {
  if (!filters) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

// Shared query-param shapes -----------------------------------------------------
type PageFilters = { page?: number; limit?: number }
/** Whitelisted server-side sort (column id + direction). */
export type SortFilters = { sortBy?: string; sortDir?: 'asc' | 'desc' }
type WindowFilters = { domainId?: string; from?: string; to?: string }
type ReportFilters = WindowFilters & { search?: string } & PageFilters
type LogFilters = {
  domainId?: string
  reportedMailId?: string
  search?: string
  startDate?: string
  endDate?: string
} & PageFilters
const jsonBody = (body: unknown): RequestInit => ({
  method: 'POST',
  body: JSON.stringify(body),
  headers: { 'Content-Type': 'application/json' },
})
const putBody = (body: unknown): RequestInit => ({
  method: 'PUT',
  body: JSON.stringify(body),
  headers: { 'Content-Type': 'application/json' },
})

// ═══════════════════════════════════════════════════════════════════════════════
// ErController — reported-mail read surface (er.controller.ts)
// ═══════════════════════════════════════════════════════════════════════════════

/** Server-side console list/rail-count params (Wave 5a). */
export type ReportedMailListParams = WindowFilters &
  PageFilters & {
    status?: string
    search?: string
    senderEmail?: string
    senderName?: string
    subject?: string
    reportedByEmail?: string
    reportedByName?: string
    dateStartFrom?: string
    dateEndsAt?: string
    hasInvalidEmailAuth?: boolean
    hasMaliciousAttachments?: boolean
    hasMaliciousLinks?: boolean
    hasBlacklistedIps?: boolean
    isPriority?: boolean
    sortBy?: string
    sortDir?: 'asc' | 'desc'
  }

export const reportedMailsApi = {
  list: (filters?: ReportedMailListParams) =>
    erFetch<ErListEnvelope<ReportedMail>>(`reported-mails${erQueryString(filters)}`),
  /** Per-rail-bucket counts over the whole filtered set (console status rail). */
  railCounts: (filters?: ReportedMailListParams) =>
    erFetch<{ label: string; value: string; count: number }[]>(
      `reported-mails/statuses${erQueryString(filters)}`,
    ),
  detail: (id: string) =>
    erFetch<ReportedMailDetail>(`reported-mails/${id}`),
  children: (id: string) =>
    erFetch<ReportedMail[]>(`reported-mails/${id}/children`),
  timeline: (id: string) =>
    erFetch<ReportedMailTimelineEntry[]>(`reported-mails/${id}/timeline`),

  // Forensic downloads (ErDownloadsController) — raw Blob streams, not JSON. -----
  downloadEml: (id: string): Promise<Blob> =>
    erDownload(`reported-mails/${id}/eml/download`),
  downloadHeaders: (id: string): Promise<Blob> =>
    erDownload(`reported-mails/${id}/headers/download`),
  downloadAttachment: (id: string, attachmentId: string): Promise<Blob> =>
    erDownload(`reported-mails/${id}/attachments/${attachmentId}/download`),
  /** Password-protected zip download of one attachment (safe for malicious files). */
  downloadAttachmentZip: (id: string, attachmentId: string) =>
    erDownloadZip(
      `reported-mails/${id}/attachments/${attachmentId}/download-zip`,
    ),

  /**
   * The status buckets + per-bucket counts that drive the NB card-list
   * status-sidebar. Sourced from the dashboard status-buckets aggregate
   * (`dashboard/reported-mails/status-buckets`) — one label/value/count per bucket.
   */
  statuses: (filters?: WindowFilters) =>
    erFetch<ErReportedMailStatusBucket[]>(
      `dashboard/reported-mails/status-buckets${erQueryString(filters)}`,
    ),

  // On-demand single-child rescans (ErController §F12/F13) --------------------
  scanUrl: (id: string, urlId: string) =>
    erFetch<ErUrlRescanResult>(
      `reported-mails/${id}/urls/${urlId}/scan`,
      { method: 'POST' },
    ),
  scanAttachment: (id: string, attachmentId: string) =>
    erFetch<ErAttachmentRescanResult>(
      `reported-mails/${id}/attachments/${attachmentId}/scan`,
      { method: 'POST' },
    ),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErRemediationController + ErNotifyController — triage write surface
// ═══════════════════════════════════════════════════════════════════════════════

/** POST reported-mails/bulk-hard-delete/preview: the combined mailbox count, the Google part, the first missing permission. */
export interface ErHardDeletePreview {
  ticketCount: number
  /** The server-recomputed count the analyst types back to confirm. */
  targetCount: number
  /** How many of those mailboxes are Google ones (a permanent delete is not available for them). */
  googleCount: number
  /** The per-action ceiling; over it the API refuses. */
  limit: number
  /** The first permission that is missing, or null when the delete may run. */
  refusal: 'role' | 'switch' | 'provider_scope' | null
}

export const remediationApi = {
  // The two bulk routes return ONE entry per ticket — an array, not a rollup.
  updateStatus: (body: ErUpdateStatusForm) =>
    erFetch<ErRemediationEnqueueResult>(
      'reported-mails/update-status',
      jsonBody(body),
    ),
  bulkUpdateStatus: (body: ErBulkUpdateStatusForm) =>
    erFetch<ErRemediationEnqueueResult[]>(
      'reported-mails/bulk-update-status',
      { ...jsonBody(body), idempotencyKey: withIdempotencyKey('er-bulk') },
    ),
  // Permanent delete (owner, 27 Oct 2026: Trash and Delete stay, behind their permissions): the role, the
  // workspace switch and the provider's permission are asked by the API in one gate, and a missing one is
  // 403 er_delete_permission_missing with a `reason`. The typed count is re-matched server-side.
  hardDelete: (body: ErUpdateStatusForm) =>
    erFetch<ErRemediationEnqueueResult>(
      'reported-mails/hard-delete',
      jsonBody(body),
    ),
  bulkHardDelete: (body: ErBulkUpdateStatusForm) =>
    erFetch<ErRemediationEnqueueResult[]>(
      'reported-mails/bulk-hard-delete',
      { ...jsonBody(body), idempotencyKey: withIdempotencyKey('er-bulk-del') },
    ),
  /** What a bulk delete would touch, and the first missing permission: the count the analyst types back. */
  previewBulkHardDelete: (body: { ids: string[]; type: ErUpdateStatusForm['type'] }) =>
    erFetch<ErHardDeletePreview>('reported-mails/bulk-hard-delete/preview', jsonBody(body)),
  notifyReporter: (id: string, body: ErNotifyReporterForm) =>
    erFetch<ErNotifyResult>(`reported-mails/${id}/notify-reporter`, jsonBody(body)),

  // Analyst reinstate decision (ErRemediationController) ----------------------
  reinstateApproved: (id: string) =>
    erFetch<ErReinstateResult>(`reported-mails/${id}/reinstate-approved`, {
      method: 'POST',
    }),
  reinstateRejected: (id: string) =>
    erFetch<ErReinstateResult>(`reported-mails/${id}/reinstate-rejected`, {
      method: 'POST',
    }),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErTriageActionsController — per-mail triage side-effects (er-triage-actions.controller.ts).
// forward-mail (200) · report-to-soc (200) · report-to-microsoft (200) ·
// remediate-org-wide (202). All POST under `er/reported-mails/:id`.
// ═══════════════════════════════════════════════════════════════════════════════

export const triageActionsApi = {
  forwardMail: (id: string) =>
    erFetch<ErForwardResult>(`reported-mails/${id}/forward-mail`, { method: 'POST' }),
  reportToSoc: (id: string) =>
    erFetch<ErNotifySocResult>(`reported-mails/${id}/report-to-soc`, { method: 'POST' }),
  reportToMicrosoft: (id: string, body?: { mailType?: ErMicrosoftMailType }) =>
    erFetch<ErReportMsResult>(
      `reported-mails/${id}/report-to-microsoft`,
      jsonBody(body ?? {}),
    ),
  remediateOrgWide: (
    id: string,
    body: { action: ErSnrAction; comment?: string },
  ) =>
    erFetch<ErOrgRemediationResult>(
      `reported-mails/${id}/remediate-org-wide`,
      jsonBody(body),
    ),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErSnrController — Search & Remediate (er-snr.controller.ts)
// ═══════════════════════════════════════════════════════════════════════════════

export const searchRemediateApi = {
  list: (filters?: PageFilters & SortFilters) =>
    erFetch<ErListEnvelope<SnrSearch>>(`search-remediate${erQueryString(filters)}`),
  start: (body: StartSnrSearchForm) =>
    erFetch<SnrSearch>('search-remediate', jsonBody(body)),
  // No `show` helper: `GET /:id` returns the same slim progress payload as
  // `GET /:id/progress` (§8.3), so `progress` covers it — a `show` typed like a
  // full row would just mislead the next caller.
  progress: (id: string) =>
    erFetch<SnrProgress>(`search-remediate/${id}/progress`),
  messages: (id: string, filters?: { status?: string } & PageFilters & SortFilters) =>
    erFetch<ErListEnvelope<SnrMessage>>(
      `search-remediate/${id}/messages${erQueryString(filters)}`,
    ),
  exportMessages: (id: string) =>
    erDownload(`search-remediate/${id}/messages/export`),
  remediationJobs: (id: string) =>
    erFetch<SnrRemediationJob[]>(`search-remediate/${id}/latest-remediation-jobs`),
  remediate: (id: string, body: SnrRemediateForm) =>
    erFetch<{ success: boolean }>(`search-remediate/${id}/remediate`, jsonBody(body)),
  remediateMessage: (messageId: string, body: SnrRemediateMessageForm) =>
    erFetch<SnrMessage>(`search-remediate/messages/${messageId}`, putBody(body)),
  pause: (id: string) =>
    erFetch<{ success: boolean }>(`search-remediate/${id}/pause`, { method: 'POST' }),
  resume: (id: string) =>
    erFetch<{ success: boolean }>(`search-remediate/${id}/resume`, { method: 'POST' }),
  cancel: (id: string) =>
    erFetch<{ success: boolean }>(`search-remediate/${id}/cancel`, { method: 'POST' }),
  cancelRemediation: (id: string) =>
    erFetch<{ success: boolean }>(`search-remediate/${id}/cancel-remediation`, {
      method: 'POST',
    }),
  pauseJob: (jobId: string) =>
    erFetch<{ success: boolean }>(`search-remediate/remediation-jobs/${jobId}/pause`, {
      method: 'POST',
    }),
  resumeJob: (jobId: string) =>
    erFetch<{ success: boolean }>(`search-remediate/remediation-jobs/${jobId}/resume`, {
      method: 'POST',
    }),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErDashboardController — aggregate overview (er-dashboard.controller.ts)
// ═══════════════════════════════════════════════════════════════════════════════

export const dashboardApi = {
  overview: (filters?: WindowFilters) =>
    erFetch<DashboardStat>(`dashboard/reported-mails${erQueryString(filters)}`),
  statusBuckets: (filters?: WindowFilters) =>
    erFetch<ErStatusBucketCount[]>(
      `dashboard/reported-mails/status-buckets${erQueryString(filters)}`,
    ),
  lastHour: (filters?: WindowFilters) =>
    erFetch<{ lastHourRemediation: number }>(
      `dashboard/reported-mails/last-hour${erQueryString(filters)}`,
    ),
  remediationTrend: (filters?: WindowFilters) =>
    erFetch<ErRemediationTrendPoint[]>(
      `dashboard/reported-mails/remediation-trend${erQueryString(filters)}`,
    ),

  // KP dashboard widgets (parity restore)
  authFailures: (filters?: WindowFilters) =>
    erFetch<ErAuthFailureChart>(
      `dashboard/reported-mails/auth-failures${erQueryString(filters)}`,
    ),
  analysis: (filters?: WindowFilters) =>
    erFetch<ErCategorySeries>(
      `dashboard/reported-mails/analysis${erQueryString(filters)}`,
    ),
  flaggedBreakdown: (filters?: WindowFilters) =>
    erFetch<ErBreakdownSlice[]>(
      `dashboard/reported-mails/flagged-breakdown${erQueryString(filters)}`,
    ),
  sourceBreakdown: (filters?: WindowFilters) =>
    erFetch<ErCategorySeries>(
      `dashboard/reported-mails/source-breakdown${erQueryString(filters)}`,
    ),
  remediationBreakdown: (filters?: WindowFilters) =>
    erFetch<ErBreakdownSlice[]>(
      `dashboard/reported-mails/remediation-breakdown${erQueryString(filters)}`,
    ),
  antiphishOverrides: (filters?: WindowFilters) =>
    erFetch<ErAntiphishOverrideRow[]>(
      `dashboard/reported-mails/antiphish-overrides${erQueryString(filters)}`,
    ),
  userGroupOverview: (filters?: WindowFilters) =>
    erFetch<ErUserGroupOverview>(
      `dashboard/user-group-overview${erQueryString(filters)}`,
    ),
  topAffectedDomains: (filters?: WindowFilters) =>
    erFetch<ErDomainStat[]>(
      `dashboard/top-affected-domains${erQueryString(filters)}`,
    ),
  monitoringDomains: (filters?: WindowFilters) =>
    erFetch<ErDomainStat[]>(
      `dashboard/monitoring-domains${erQueryString(filters)}`,
    ),
  threatLogsDashboard: (filters?: WindowFilters) =>
    erFetch<ErThreatLogDashboard>(
      `dashboard/threat-logs${erQueryString(filters)}`,
    ),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErReportsController — reports (er-reports.controller.ts)
// ═══════════════════════════════════════════════════════════════════════════════

export const reportsApi = {
  reportedMails: (filters?: ReportFilters) =>
    erFetch<ErListEnvelope<ErReportRow>>(
      `reports/reported-mails${erQueryString(filters)}`,
    ),
  exportReportedMails: (filters?: ReportFilters) =>
    erDownload(`reports/reported-mails/export${erQueryString(filters)}`),
  threatLogs: (filters?: ReportFilters) =>
    erFetch<ErListEnvelope<ErReportRow>>(
      `reports/threat-logs${erQueryString(filters)}`,
    ),
  exportThreatLogs: (filters?: ReportFilters) =>
    erDownload(`reports/threat-logs/export${erQueryString(filters)}`),
  remediationStatusChanges: (filters?: WindowFilters) =>
    erFetch<ErBreakdownRow[]>(
      `reports/remediation-status-changes${erQueryString(filters)}`,
    ),
  remediationDispositions: (filters?: WindowFilters) =>
    erFetch<ErBreakdownRow[]>(
      `reports/remediation-dispositions${erQueryString(filters)}`,
    ),
}

// ═══════════════════════════════════════════════════════════════════════════════
// Threat Logs — the standalone NB threat-logs screen surface (§4.11).
//
// The BE serves threat logs THROUGH the reports controller (GET /er/reports/threat-
// logs) as an `ErListEnvelope`, but returns the FULL `er_threat_logs` row (bare
// `select()`), so we type it richer than the lossy `ErReportRow` — as `ErThreatLog`
// — to keep `reportedMailId` / `blacklists` / `details` / DNS-auth domains that the
// NB detail-view needs.
//
// INTEGRATE NOTE (missing BE endpoints): there is NO `GET /er/threat-logs/:id`
// single-log read and NO threat-log-specific Archive/Quarantine write. `list` here
// re-targets the reports list; `detail` is resolved cache-first by the hook layer
// (see use-er-threat-log-detail.ts). Archive/Quarantine is mapped onto the LINKED
// reported-mail via `remediationApi.updateStatus` (Archive/Quarantine are valid
// manual remediation statuses) — a threat log with no `reportedMailId` cannot be
// actioned until a dedicated endpoint lands.
// ═══════════════════════════════════════════════════════════════════════════════

export const threatLogsApi = {
  /** Paginated threat-logs list — the FULL row (superset of `ErReportRow`). */
  list: (filters?: ReportFilters) =>
    erFetch<ErListEnvelope<ErThreatLog>>(
      `reports/threat-logs${erQueryString(filters)}`,
    ),
  /** Stream the threat-logs report as a CSV attachment. */
  exportCsv: (filters?: ReportFilters) =>
    erDownload(`reports/threat-logs/export${erQueryString(filters)}`),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErLeaderboardController — leaderboard (er-leaderboard.controller.ts)
// ═══════════════════════════════════════════════════════════════════════════════

export const leaderboardApi = {
  list: (filters?: WindowFilters & { limit?: number }) =>
    erFetch<LeaderboardResponse>(`leaderboard${erQueryString(filters)}`),
  stats: (filters?: WindowFilters) =>
    erFetch<LeaderboardStats>(`leaderboard/stats${erQueryString(filters)}`),
  export: (filters?: WindowFilters & { limit?: number }) =>
    erDownload(`leaderboard/export${erQueryString(filters)}`),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErLogsController — email / notification / banner / AI action logs
// ═══════════════════════════════════════════════════════════════════════════════

export const logsApi = {
  emailLogs: (filters?: LogFilters) =>
    erFetch<ErListEnvelope<EmailLog>>(`email-logs${erQueryString(filters)}`),
  exportEmailLogs: (filters?: LogFilters) =>
    erDownload(`email-logs/export${erQueryString(filters)}`),
  notificationLogs: (filters?: LogFilters) =>
    erFetch<ErListEnvelope<NotificationLog>>(
      `notification-logs${erQueryString(filters)}`,
    ),
  exportNotificationLogs: (filters?: LogFilters) =>
    erDownload(`notification-logs/export${erQueryString(filters)}`),
  bannerLogs: (filters?: LogFilters) =>
    erFetch<ErListEnvelope<BannerLog>>(`banner-logs${erQueryString(filters)}`),
  bannerLogUsers: (id: string) =>
    erFetch<BannerLogUser[]>(`banner-logs/${id}/users`),
  removeBannerForUser: (id: string, uid: string) =>
    erFetch<{ removed: boolean; skippedReason: string | null }>(
      `banner-logs/${id}/users/${uid}/remove-banner`,
      { method: 'POST' },
    ),
  aiActionLogs: (filters?: LogFilters) =>
    erFetch<ErListEnvelope<AiActionLog>>(`ai-action-logs${erQueryString(filters)}`),
  exportAiActionLogs: (filters?: LogFilters) =>
    erDownload(`ai-action-logs/export${erQueryString(filters)}`),
  undoAiAction: (id: string) =>
    erFetch<{ reversed: boolean; reason: string | null }>(
      `ai-action-logs/${id}/undo`,
      { method: 'POST' },
    ),
}

// ═══════════════════════════════════════════════════════════════════════════════
// ErController (bare settings) + ErSettingsController — policy / catalogs / templates
// ═══════════════════════════════════════════════════════════════════════════════

/** GET /er/settings/policy: the BaseSettingsService envelope (C13 — the type said a flat policy). */
export interface ErPolicyEnvelope {
  settings: ErPolicy
  _fieldSources?: ErPolicy['_fieldSources']
}

export const settingsApi = {
  /** Materialized workspace ER policy singleton (bare GET /er/settings). */
  getSettings: () => erFetch<ErPolicy>('settings'),
  /**
   * Resolved workspace policy + _fieldSources (GET /er/settings/policy) — the
   * shared settings envelope, not a flat policy (useErPolicy flattens it).
   */
  getPolicy: () => erFetch<ErPolicyEnvelope>('settings/policy'),
  updatePolicy: (body: UpdateErSettingsForm) =>
    erFetch<ErPolicy>('settings', putBody(body)),

  getReporterMessages: () => erFetch<ReporterMessage>('reporter-messages'),
  updateReporterMessages: (body: UpdateReporterMessagesForm) =>
    erFetch<ReporterMessage>('reporter-messages', putBody(body)),

  getAddinActions: () => erFetch<AddinAction>('addin-actions'),
  updateAddinActions: (body: UpdateAddinActionsForm) =>
    erFetch<AddinAction>('addin-actions', putBody(body)),

  getReporterPoints: (filters?: { domainId?: string }) =>
    erFetch<ReporterPoint>(`reporter-points${erQueryString(filters)}`),
  updateReporterPoints: (body: UpdateReporterPointsForm) =>
    erFetch<ReporterPoint>('reporter-points', putBody(body)),
}

/**
 * Add-in white-label branding (Wave C · Ca4 / Cb). The editor GET returns the
 * resolved blob + raw seeds + `_fieldSources` + `useCompanyDefaults`/`overridden
 * Fields`; the workspace PUT is a sparse patch (contrast-gated, override recompute);
 * the company-default PUT writes the inheritance base (a company admin). The logo
 * upload is multipart (server rasterizes + persists the URL onto the workspace
 * branding, returning the fresh editor blob). `preview` is a dry-run derivation
 * (no persist) that backs the live task-pane preview.
 */
export const brandingApi = {
  getSettings: () =>
    erFetch<ErAddinBrandingEditorResponse>('settings/addin-branding'),
  updateWorkspace: (body: UpdateErAddinBrandingForm) =>
    erFetch<ErAddinBrandingUpdateResponse>('settings/addin-branding', putBody(body)),
  updateCompanyDefault: (body: UpdateErAddinBrandingForm) =>
    erFetch<{ branding: ErAddinBrandingForEditor; warnings: ErAddinContrastViolation[] }>(
      'settings/addin-branding/company-default',
      putBody(body),
    ),
  uploadLogo: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return erUpload<ErAddinBrandingEditorResponse>('settings/addin-branding/logo', form)
  },
  preview: (body: UpdateErAddinBrandingForm) =>
    erFetch<ErAddinBrandingResolved>('settings/addin-branding/preview', jsonBody(body)),
}

export const dispositionsApi = {
  list: (filters?: { domainId?: string }) =>
    erFetch<Disposition[]>(`dispositions${erQueryString(filters)}`),
  create: (body: CreateErDispositionForm) =>
    erFetch<Disposition>('dispositions', jsonBody(body)),
  update: (id: string, body: UpdateErDispositionForm) =>
    erFetch<Disposition>(`dispositions/${id}`, putBody(body)),
  setStatus: (id: string, status: 'Active' | 'Inactive') =>
    erFetch<Disposition>(`dispositions/${id}/status`, jsonBody({ status })),
  remove: (id: string) =>
    erFetch<void>(`dispositions/${id}`, { method: 'DELETE' }),
}

export const eventActionsApi = {
  list: (filters?: { domainId?: string }) =>
    erFetch<EventAction[]>(`event-actions${erQueryString(filters)}`),
  create: (body: CreateErEventActionForm) =>
    erFetch<EventAction>('event-actions', jsonBody(body)),
  update: (id: string, body: UpdateErEventActionForm) =>
    erFetch<EventAction>(`event-actions/${id}`, putBody(body)),
  reorder: (body: ReorderErEventActionsForm) =>
    erFetch<EventAction[]>('event-actions/reorder', jsonBody(body)),
  remove: (id: string) =>
    erFetch<void>(`event-actions/${id}`, { method: 'DELETE' }),
}

export const trustedDomainsApi = {
  list: (filters?: { domainId?: string }) =>
    erFetch<TrustedDomain[]>(`trusted-domains${erQueryString(filters)}`),
  create: (body: CreateErTrustedDomainForm) =>
    erFetch<TrustedDomain>('trusted-domains', jsonBody(body)),
  remove: (id: string) =>
    erFetch<void>(`trusted-domains/${id}`, { method: 'DELETE' }),
}

export const integrationsApi = {
  list: (filters?: { domainId?: string }) =>
    erFetch<ErIntegration[]>(`integrations${erQueryString(filters)}`),
  setReportToMicrosoft: (body: ReportToMicrosoftConfigForm) =>
    erFetch<ErIntegration>('integrations/report-to-microsoft', jsonBody(body)),
  setSocRecipients: (body: SocRecipientsConfigForm) =>
    erFetch<ErIntegration>('integrations/soc', jsonBody(body)),
  setStatus: (slug: string, body: { status: 'Active' | 'Inactive'; domainId?: string }) =>
    erFetch<ErIntegration>(`integrations/${slug}/status`, jsonBody(body)),
  upsert: (slug: string, body: UpdateErIntegrationConfigForm) =>
    erFetch<ErIntegration>(`integrations/${slug}`, jsonBody(body)),
}

export const templatesApi = {
  list: (filters?: { domainId?: string; type?: string }) =>
    erFetch<ErTemplate[]>(`templates${erQueryString(filters)}`),
  detail: (id: string) => erFetch<ErTemplate>(`templates/${id}`),
  create: (body: CreateErTemplateForm) =>
    erFetch<ErTemplate>('templates', jsonBody(body)),
  update: (id: string, body: UpdateErTemplateForm) =>
    erFetch<ErTemplate>(`templates/${id}`, putBody(body)),
  setStatus: (id: string, status: 'Active' | 'Inactive') =>
    erFetch<ErTemplate>(`templates/${id}/status`, jsonBody({ status })),
  reset: (id: string) =>
    erFetch<ErTemplate>(`templates/${id}/reset`, { method: 'POST' }),
  remove: (id: string) =>
    erFetch<void>(`templates/${id}`, { method: 'DELETE' }),
  getSettings: (filters?: { domainId?: string }) =>
    erFetch<ErDefaultTemplateSettings[]>(`templates/settings${erQueryString(filters)}`),
  updateSettings: (body: UpdateErDefaultTemplateSettingsForm) =>
    erFetch<ErDefaultTemplateSettings>('templates/settings', putBody(body)),
}

/**
 * ER per-domain notification-template overrides (V4 Domain tier). Resolves on
 * Domain → Workspace → Company → Global; edit = copy-on-write for the selected
 * domain, reset = delete the domain override → re-inherit. Keyed by notification key.
 */
export const notificationTemplatesApi = {
  list: (domainId?: string) =>
    erFetch<ErEffectiveTemplate[]>(
      `notification-templates${erQueryString({ domainId })}`,
    ),
  saveOverride: (
    key: string,
    domainId: string,
    body: { subject: string; bodyHtml: string },
  ) =>
    erFetch<ErEffectiveTemplate>(
      `notification-templates/${encodeURIComponent(key)}${erQueryString({ domainId })}`,
      putBody(body),
    ),
  resetOverride: (key: string, domainId: string) =>
    erFetch<{ reset: boolean }>(
      `notification-templates/${encodeURIComponent(key)}${erQueryString({ domainId })}`,
      { method: 'DELETE' },
    ),
}

/** Namespaced ER API surface — one object per controller group. */
export const erApi = {
  reportedMails: reportedMailsApi,
  remediation: remediationApi,
  triageActions: triageActionsApi,
  searchRemediate: searchRemediateApi,
  dashboard: dashboardApi,
  reports: reportsApi,
  threatLogs: threatLogsApi,
  leaderboard: leaderboardApi,
  logs: logsApi,
  settings: settingsApi,
  branding: brandingApi,
  dispositions: dispositionsApi,
  eventActions: eventActionsApi,
  trustedDomains: trustedDomainsApi,
  integrations: integrationsApi,
  templates: templatesApi,
  notificationTemplates: notificationTemplatesApi,
}
